import React, { FC, useCallback, useEffect, useState } from 'react';

import { Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { getEventOfferRoute } from 'routes/events/list';
import { Button, DatePicker, Form, Label, Link, Typography, message } from 'ui';
import { formRules } from 'utils/formRules';

import { activeEventNameSelector } from 'selectors/eventsSlice.selector';
// import { createEventFormActiveStepSelector } from 'selectors/eventsSlice.selector';
import { useLazyGetEventDetailQuery } from 'services/events/eventsApiService';
import {
  useCreateOfferMutation,
  useLazyGetOffersQuery,
  useUpdateOfferMutation,
} from 'services/promoter/promoterApiService';
import { useLazyGetCurrentUserQuery } from 'services/user-management/userManagementApiService';
import { removeActiveEvent, setActiveEvent, setEditingEventId } from 'slices/eventsSlice';
import { useAppDispatch, useAppSelector } from 'store';

import { EventCreatedType, EventTicketType } from 'types/events/eventTypes';
import { EventOffer, OfferType } from 'types/promoters/promoterTypes';

import EventOfferFixedDiscount from '../event-offer-fixed-discount';
import EventOfferFixedPriceSpecificTypes from '../event-offer-fixed-price-specific-types';
import EventOfferFixedPriceTicketType from '../event-offer-fixed-price-ticket-type';
import EventOfferGreaterNumberTickets from '../event-offer-greater-number-tickets';
import EventOfferPerEvent from '../event-offer-per-event';
import EventOfferPerMultipleTickets from '../event-offer-per-multiple-tickets';
import EventOfferPerShow from '../event-offer-per-show';
import EventOfferShowRestriction from '../event-offer-show-restriction';
import EventOfferType from '../event-offer-type';
import s from './EventOfferContainer.module.scss';

const EventOfferContainer: FC = () => {
  // const activeStep = useAppSelector(createEventFormActiveStepSelector);
  const { eventId, offerId } = useParams();
  const navigate = useNavigate();
  const [form] = useForm();
  const [getEventDetail, { data: eventDetail }] = useLazyGetEventDetailQuery();
  const [getCurrentUser, { data: currentUser }] = useLazyGetCurrentUserQuery();
  const [createOffer, { isLoading: creating }] = useCreateOfferMutation();
  const [updateOffer, { isLoading: updating }] = useUpdateOfferMutation();
  const [getOffers, { data: offers }] = useLazyGetOffersQuery();
  const dispatch = useAppDispatch();
  const [editingOffer, setEditingOffer] = useState<EventOffer>();
  const eventName = useAppSelector(activeEventNameSelector);

  const [showRestrictions, setShowRestrictions] = useState<string[]>();

  const [submitTicketTypes, setSubmitTicketTypes] = useState<
    {
      id: string;
      ticketType: EventTicketType;
      priceToBeSold?: number;
      minimumNumberOfTicketsToBeBought?: number;
    }[]
  >([]);
  const [selectedOfferType, setSelectedOfferType] = useState(
    OfferType.fixedPriceOnSpecificTicketTypes,
  );

  const handleGetEditingEvent = useCallback(
    async (eventId: string) => {
      const res = await getEventDetail(eventId);

      if ('data' in res) {
        dispatch(setActiveEvent(res.data as EventCreatedType));
      }
    },
    [dispatch, getEventDetail],
  );

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  useEffect(() => {
    if (eventId) {
      getOffers({ eventId: eventId });
    }
  }, [eventId, getOffers]);

  useEffect(() => {
    if (offers) {
      setEditingOffer(offers?.offers?.find((item) => item.id === offerId));
    }
  }, [offers, offerId]);

  useEffect(() => {
    if (editingOffer) {
      setSelectedOfferType(editingOffer?.offerType);
      const transformed = editingOffer?.ticketTypes
        .map((type) => {
          const matchingTicket = eventDetail?.ticketTypes.find(
            (ticket) => ticket.id === type.ticketTypeId,
          );
          if (!matchingTicket) return null; // Skip if no match found

          return {
            id: type.id,
            ticketType: matchingTicket,
            priceToBeSold: type.priceToBeSold,
            minimumNumberOfTicketsToBeBought: type.minimumNumberOfTicketsToBeBought,
          };
        })
        .filter(Boolean) as any[];
      setSubmitTicketTypes(transformed);
      const tempshowIds = editingOffer?.restrictToShows.map((item) => item.showId);
      const tempEventLinks = editingOffer?.eventLinks?.map((link) => link.eventId);
      setShowRestrictions(tempshowIds);
      form.setFieldsValue({
        name: editingOffer?.name,
        description: editingOffer?.description,
        offerType: editingOffer?.offerType,
        active: editingOffer?.active,
        dateFrom: editingOffer?.dateFrom ? dayjs(editingOffer.dateFrom) : null,
        dateTo: editingOffer?.dateTo ? dayjs(editingOffer.dateTo) : null,
        numberOfTicketsAvailable: editingOffer?.numberOfTicketsAvailable,
        fixedDiscount: editingOffer?.fixedDiscount,
        numberOfTicketsToGetDiscount: editingOffer?.numberOfTicketsToGetDiscount,
        numberOfTicketsToGetDiscountOn: editingOffer?.numberOfTicketsToGetDiscountOn,
        fixedDiscountValueType: editingOffer?.fixedDiscountValueType,
        minimumNumberOfShows: editingOffer?.minimumNumberOfShows,

        // Optional: set IDs or nested fields for additional handling
        ticketTypes: editingOffer?.ticketTypes?.map((tt) => ({
          ticketTypeId: tt.ticketTypeId,
          priceToBeSold: tt.priceToBeSold,
        })),

        eventLinks: tempEventLinks || [],
      });
    }
  }, [editingOffer, form, eventDetail?.ticketTypes]);

  useEffect(() => {
    if (eventId) {
      dispatch(setEditingEventId(eventId));
      handleGetEditingEvent(eventId);
    }
  }, [dispatch, eventId, handleGetEditingEvent]);

  useEffect(() => {
    return () => {
      dispatch(removeActiveEvent());
    };
  }, [dispatch]);

  const handleSubmit = useCallback(
    async (data) => {
      if (submitTicketTypes.length === 0 && selectedOfferType !== OfferType.fixedDiscount) {
        message.warning('Please select ticket types.');
        return;
      }
      const tempShows = showRestrictions?.map((item) => ({ showId: item.toLowerCase() }));
      const tempEvents = data.eventLinks?.map((item) => ({ eventId: item.toLowerCase() }));
      const tempTicketTypes = submitTicketTypes?.map((item) => ({
        ticketTypeId: item.ticketType.id,
        newPrice: item.priceToBeSold,
        minimumNumberOfTickets: item.minimumNumberOfTicketsToBeBought,
      }));

      const result = {
        ...data,
        ticketTypes: selectedOfferType === OfferType.fixedDiscount ? [] : tempTicketTypes,
        eventId: eventId,
        offerType: selectedOfferType,
        eventLinks: tempEvents,
        showRestrictions: tempShows,
      };

      if (
        result.active === true &&
        offers?.offers?.some((item) => item.id !== offerId && item.active === true)
      ) {
        message.warning('Only one offer can be enabled at a time.');
        return;
      }
      if (offerId === 'create') {
        const res = await createOffer(result);
        if ('data' in res) {
          message.success('Successfully created offer.');
          navigate(getEventOfferRoute(eventDetail?.eventId ?? ''));
        }
      } else {
        const res = await updateOffer({ id: offerId ?? '', data: result });
        if ('data' in res) {
          message.success('Successfully updated offer.');
          navigate(getEventOfferRoute(eventDetail?.eventId ?? ''));
        }
      }
    },
    [
      navigate,
      eventDetail?.eventId,
      eventId,
      selectedOfferType,
      offers?.offers,
      showRestrictions,
      createOffer,
      updateOffer,
      offerId,
      submitTicketTypes,
    ],
  );

  const content = {
    fixedPriceOnSpecificTicketTypes: (
      <EventOfferFixedPriceSpecificTypes
        tickets={eventDetail?.ticketTypes}
        ticketTypes={submitTicketTypes.map((item) => ({
          ...item,
          ticketTypeId: item?.ticketType.id, // or any value you want to include
        }))}
        setTicketTypes={setSubmitTicketTypes}
      />
    ),
    fixedPriceOnAllTicketTypes: (
      <EventOfferFixedPriceTicketType
        tickets={eventDetail?.ticketTypes}
        ticketTypes={submitTicketTypes.map((item) => ({
          ...item,
          ticketTypeId: item?.ticketType.id, // or any value you want to include
        }))}
        setTicketTypes={setSubmitTicketTypes}
      />
    ),
    greaterOrEqualToNumberOfTickets: (
      <EventOfferGreaterNumberTickets
        tickets={eventDetail?.ticketTypes}
        ticketTypes={submitTicketTypes.map((item) => ({
          ...item,
          ticketTypeId: item?.ticketType.id, // or any value you want to include
        }))}
        setTicketTypes={setSubmitTicketTypes}
      />
    ),
    perMultipleNumberOfTickets: (
      <EventOfferPerMultipleTickets
        tickets={eventDetail?.ticketTypes}
        ticketTypes={submitTicketTypes.map((item) => ({
          ...item,
          ticketTypeId: item?.ticketType.id, // or any value you want to include
        }))}
        setTicketTypes={setSubmitTicketTypes}
      />
    ),
    fixedDiscount: <EventOfferFixedDiscount />,
    perDifferentShowNames: (
      <EventOfferPerShow
        tickets={eventDetail?.ticketTypes}
        ticketTypes={submitTicketTypes.map((item) => ({
          ...item,
          ticketTypeId: item?.ticketType.id, // or any value you want to include
        }))}
        setTicketTypes={setSubmitTicketTypes}
      />
    ),
    perDifferentEvents: (
      <EventOfferPerEvent
        tickets={eventDetail?.ticketTypes}
        ticketTypes={submitTicketTypes.map((item) => ({
          ...item,
          ticketTypeId: item?.ticketType.id, // or any value you want to include
        }))}
        setTicketTypes={setSubmitTicketTypes}
      />
    ),
  };

  return (
    <Form form={form} onFinish={handleSubmit}>
      <div className={s.wrapper}>
        <Link
          to={getEventOfferRoute(eventDetail?.eventId ?? '')}
          icon="arrowLeft"
          label="Event Offers"
          labelType="h6"
          className={s.backLink}></Link>

        <div className={s.header}>
          {eventName && (
            <Typography type="h2" weight={700}>
              &quot;{eventName ?? ''}&quot;&nbsp;Event Offers
            </Typography>
          )}
        </div>

        <EventOfferType setOfferType={setSelectedOfferType} offerType={selectedOfferType} />
        {content[selectedOfferType]}

        <div className={s.row}>
          <div className={s.item}>
            <Label>Date from (if any)</Label>
            <Form.Item name="dateFrom">
              <DatePicker fullWidth format="ddd DD MMMM YYYY" />
            </Form.Item>
          </div>
          <div className={s.item}>
            <Label>Date to (if any)</Label>
            <Form.Item name="dateTo">
              <DatePicker fullWidth format="ddd DD MMMM YYYY" />
            </Form.Item>
          </div>
        </div>
        {selectedOfferType !== OfferType.perDifferentShowNames && eventDetail && (
          <div className={s.row}>
            <div className={s.item}>
              <EventOfferShowRestriction
                showRestrictions={showRestrictions}
                setShowRestrictions={setShowRestrictions}
                shows={eventDetail?.basicInfo?.showDates}
              />
            </div>
          </div>
        )}

        {selectedOfferType === OfferType.perDifferentEvents && (
          <div className={s.row}>
            <div className={s.item}>
              <Label required>Event Links</Label>
              <Form.Item rules={[formRules.required]} name="eventLinks">
                <Select
                  mode="multiple"
                  style={{ width: '100%', height: 40 }}
                  options={currentUser?.events?.map((item) => ({
                    label: item.eventName,
                    value: item.eventId,
                  }))}
                />
              </Form.Item>
            </div>
          </div>
        )}

        <Button
          htmlType="submit"
          loading={creating || updating}
          style={{ marginTop: 24, marginBottom: 16, width: 200 }}>
          {offerId === 'create' ? 'CREATE' : 'SAVE'}
        </Button>
      </div>
    </Form>
  );
};

export default EventOfferContainer;
